/**
 * Google Analytics React Hook
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { analytics } from '../services/analyticsService';
import { log } from '../utils/logger';

/**
 * 自动追踪页面浏览的 Hook
 */
export function usePageTracking() {
  const location = useLocation();

  useEffect(() => {
    // 等待一小段时间确保页面完全加载
    const timer = setTimeout(() => {
      const path = location.pathname + location.search;
      const title = document.title;
      
      analytics.trackPageView(path, title);
      log.debug('页面浏览追踪:', { path, title });
    }, 100);

    return () => clearTimeout(timer);
  }, [location]);
}

/**
 * 手动页面追踪 Hook
 */
export function useManualPageTracking() {
  const trackPage = (path?: string, title?: string) => {
    analytics.trackPageView(path, title);
  };

  return { trackPage };
}

/**
 * 事件追踪 Hook
 */
export function useEventTracking() {
  const trackEvent = (eventName: string, params?: Record<string, any>) => {
    analytics.trackEvent(eventName, params);
  };

  const trackLogin = (method?: string) => {
    analytics.trackLogin(method);
  };

  const trackSignUp = (method?: string) => {
    analytics.trackSignUp(method);
  };

  const trackLogout = () => {
    analytics.trackLogout();
  };

  const trackOAuthLogin = {
    attempt: (provider: string) => analytics.trackOAuthLoginAttempt(provider),
    success: (provider: string) => analytics.trackOAuthLoginSuccess(provider),
    error: (provider: string, error: string) => analytics.trackOAuthLoginError(provider, error),
  };

  const trackEcommerce = {
    viewPricing: () => analytics.trackViewPricing(),
    beginCheckout: (currency?: string, value?: number) => analytics.trackBeginCheckout(currency, value),
    purchase: (transactionId: string, value: number, currency?: string) => 
      analytics.trackPurchase(transactionId, value, currency),
    cancelSubscription: (reason?: string) => analytics.trackSubscriptionCancel(reason),
  };

  const trackEngagement = {
    viewFeatures: () => analytics.trackViewFeatures(),
    viewProfile: () => analytics.trackProfileView(),
  };

  const trackError = (error: string, category?: string) => {
    analytics.trackError(error, category);
  };

  return {
    trackEvent,
    trackLogin,
    trackSignUp,
    trackLogout,
    trackOAuthLogin,
    trackEcommerce,
    trackEngagement,
    trackError,
  };
}

/**
 * 用户属性设置 Hook
 */
export function useUserTracking() {
  const setUserProperties = (properties: Record<string, any>) => {
    analytics.setUserProperties(properties);
  };

  const setUserId = (userId: string) => {
    analytics.setUserId(userId);
  };

  return {
    setUserProperties,
    setUserId,
  };
}